import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';

import { redirectGuard } from './guard/redirect.guard';
import { loginRedirectGuard } from './guard/login-redirect.guard';
import { authGuard } from './guard/auth.guard';
import { folioGuard } from './guard/folio.guard';
import { queryGuard } from './guard/query.guard';
import { marketGuard } from './guard/market.guard';
import { marketBoardGuard } from './guard/market-board.guard';
import { searchGuard } from './guard/search.guard';
import { newsGuard } from './guard/news.guard';
import { trendGuard } from './guard/trend.guard';
import { filtersGuard } from './guard/filters.guard';
import { chatGuard } from './guard/chat.guard';

import { LoginComponent } from './user/login/login.component';
import { MainNewsComponent } from './cockpit/main-news/main-news.component';
import { MessageComponent } from './user/message/message.component';
import { NewsMarketBoardComponent } from './cockpit/main-news/news-market-board/news-market-board.component';
import { WidgetIframeComponent } from './project/widget/widget-iframe/widget-iframe.component';
import { PasswordComponent } from './user/password/password.component';
import { NewsChartmapComponent } from './cockpit/main-news/news-chartmap/news-chartmap.component';
import { QueryFolioComponent } from './cockpit/main-news/news-query/query-folio/query-folio.component';
import { NewsOutletComponent } from './cockpit/main-news/news-outlet/news-outlet.component';
import { PortfolioChartComponent } from './cockpit/main-news/news-portfolio/portfolio-chart/portfolio-chart.component';
import { DynamicContainerComponent } from './container/dynamic-container.component';
import { CommodityReportsComponent } from './cockpit/commodity-reports/commodity-reports.component';
import { DueDiligenceComponent } from './cockpit/due-diligence/due-diligence.component';
import { commodityReportsGuard } from './guard/commodity-reports.guard';
import { dueDiligenceGuard } from './guard/due-diligence.guard';
import { NewsAuditViewComponent } from './cockpit/main-news/news-audit-view/news-audit-view.component';

const routes: Routes = [
  { path: '', redirectTo: 'cockpit', pathMatch: 'full' },

  { path: 'iframe', redirectTo: 'widget', pathMatch: 'full' },
  { path: 'logout', redirectTo: '/message/logout', pathMatch: 'full' },
  {
    path: 'cockpit',
    component: DynamicContainerComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'news', pathMatch: 'full' },
      {
        path: 'news',
        component: NewsOutletComponent,
        canActivate: [redirectGuard, newsGuard, filtersGuard],
        children: [
          { path: '', redirectTo: 'portfolio', pathMatch: 'full' },

          {
            path: 'sector',
            component: NewsMarketBoardComponent,
            canActivate: [marketBoardGuard],
          },

          {
            path: 'market',
            component: MainNewsComponent,
            canActivate: [marketGuard],
            runGuardsAndResolvers: 'always',
            children: [
              { path: 'chart', component: NewsChartmapComponent },
              { path: 'audit', component: NewsAuditViewComponent },
            ],
          },

          {
            path: 'portfolio',
            component: MainNewsComponent,
            canActivate: [redirectGuard, folioGuard],
            runGuardsAndResolvers: 'always',
            children: [
              { path: '', redirectTo: 'scanner', pathMatch: 'full' },
              {
                path: 'scanner',
                loadChildren: () =>
                  import('./cockpit/scanner/scanner.module').then(
                    (m) => m.ScannerModule,
                  ),
              },
              { path: 'main', redirectTo: 'scanner', pathMatch: 'full' },
              { path: 'benchmark', component: PortfolioChartComponent },
              { path: 'chart', component: NewsChartmapComponent },
              { path: 'audit', component: NewsAuditViewComponent },
            ],
          },

          {
            path: 'query',
            component: MainNewsComponent,
            canActivate: [queryGuard],
            runGuardsAndResolvers: 'always',
            children: [
              { path: '', redirectTo: 'main', pathMatch: 'full' },
              { path: 'main', component: QueryFolioComponent },
              { path: 'chart', component: NewsChartmapComponent },
            ],
          },

          {
            path: 'search',
            component: MainNewsComponent,
            canActivate: [searchGuard],
            children: [
              { path: '', redirectTo: 'chart', pathMatch: 'full' },
              { path: 'main', component: NewsChartmapComponent },
              { path: 'chart', component: NewsChartmapComponent },
              { path: 'audit', component: NewsAuditViewComponent },
            ],
          },
        ],
      },

      {
        path: 'invest',
        canActivate: [trendGuard],
        loadChildren: () =>
          import('./cockpit/trend/trend.module').then((m) => m.TrendModule),
      },

      {
        path: 'chat',
        canActivate: [chatGuard],
        loadChildren: () =>
          import('./project/llm/chat.module').then((m) => m.ChatModule),
      },
      {
        path: 'newsletter',
        canActivate: [authGuard],
        loadChildren: () =>
          import('./project/newsletter/newsletter.module').then(
            (m) => m.NewsletterModule,
          ),
      },
      {
        path: 'reports',
        children: [
          { path: '', redirectTo: 'commodities', pathMatch: 'full' },
          {
            path: 'commodities',
            component: CommodityReportsComponent,
            canActivate: [commodityReportsGuard],
          },
          {
            path: 'due-diligence',
            component: DueDiligenceComponent,
            canActivate: [dueDiligenceGuard],
          },
        ],
      },
    ],
  },

  {
    path: 'widget/reader',
    component: WidgetIframeComponent,
    canActivate: [authGuard, redirectGuard],
    runGuardsAndResolvers: 'always',
  },
  {
    path: 'widget/story',
    component: WidgetIframeComponent,
    canActivate: [authGuard, redirectGuard],
    runGuardsAndResolvers: 'always',
  },

  {
    path: 'login',
    component: LoginComponent,
    canActivate: [loginRedirectGuard],
  },
  { path: 'password', component: PasswordComponent },
  { path: 'password/:id', component: PasswordComponent },
  { path: 'message/:id', component: MessageComponent },

  { path: '**', redirectTo: 'message/error404', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
